// @ts-nocheck
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { getInitials } from '@/utils/initials';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, List, Users, Activity, Plug,
  Plus, X, Euro, Building2, User, Calendar, ChevronDown,
  Loader2, AlertCircle, DollarSign, TrendingUp, Target, Award,
  Phone, Mail, Clock, RefreshCw, Globe, ExternalLink, ArrowLeft, Lightbulb, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDialer } from '@/contexts/DialerContext';
import { useTranslation } from 'react-i18next';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { api } from '@/api/api-client';
import { listCustomers } from '@/api/endpoints/customers';
import CRMActivityFeed from '@/components/dialer/CRMActivityFeed';
import SwoftActivityFeed from '@/components/dialer/SwoftActivityFeed';
import { formatCurrency, formatDate as formatDateInLocale, formatDayMonthYear } from '@/i18n/locale';
import { errorMessage } from '@/services/errorMessage';
import { crmProviderShown } from '@/lib/hubspotUi';
import { PipelineKanban, PipelineStats } from '@/components/crm/PipelineKanban';
import OutcomeMappingPanel from '@/components/crm/OutcomeMappingPanel';
import { CrmTabBar, crmPanelId, crmTabId, resolveCrmTab } from '@/components/crm/CrmTabBar';
import { useAuthStore } from '@/stores/authStore';
import { ListChecks } from 'lucide-react';

// ─── Deal stages ─────────────────────────────────────────────────────────────

// `labelKey` is resolved with t() at render time: a module-level constant
// cannot call the translation hook.
const DEAL_STAGES = [
  { id: 'decouverte',         labelKey: 'pages:crm.stages.decouverte',         color: 'bg-slate-100 text-slate-700 border-slate-200',   dot: 'bg-slate-400',   header: 'bg-slate-50',   border: 'border-slate-200',   badge: 'bg-slate-100 text-slate-600' },
  { id: 'call_demo',          labelKey: 'pages:crm.stages.callDemo',           color: 'bg-blue-100 text-blue-700 border-blue-200',      dot: 'bg-blue-500',    header: 'bg-blue-50',    border: 'border-blue-200',    badge: 'bg-blue-100 text-blue-700' },
  { id: 'proposition_envoyee',labelKey: 'pages:crm.stages.propositionSent',     color: 'bg-violet-100 text-violet-700 border-violet-200',dot: 'bg-violet-500',  header: 'bg-violet-50',  border: 'border-violet-200',  badge: 'bg-violet-100 text-violet-700' },
  { id: 'negociation',        labelKey: 'pages:crm.stages.negotiation',        color: 'bg-amber-100 text-amber-700 border-amber-200',   dot: 'bg-amber-500',   header: 'bg-amber-50',   border: 'border-amber-200',   badge: 'bg-amber-100 text-amber-700' },
  { id: 'ferme_gagne',        labelKey: 'pages:crm.stages.closedWon',          color: 'bg-green-100 text-green-700 border-green-200',   dot: 'bg-green-500',   header: 'bg-green-50',   border: 'border-green-200',   badge: 'bg-green-100 text-green-700' },
  { id: 'ferme_perdu',        labelKey: 'pages:crm.stages.closedLost',         color: 'bg-red-100 text-red-700 border-red-200',         dot: 'bg-red-500',     header: 'bg-red-50',     border: 'border-red-200',     badge: 'bg-red-100 text-red-700' },
];

const STAGE_BY_ID = Object.fromEntries(DEAL_STAGES.map(s => [s.id, s]));

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatAmount(amount) {
  if (amount === null || amount === undefined || amount === '') return '—';
  return formatCurrency(Number(amount));
}

const AVATAR_COLORS = ['bg-blue-500', 'bg-teal-500', 'bg-violet-500', 'bg-pink-500', 'bg-orange-500', 'bg-indigo-500'];
function avatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ─── New Deal Dialog ──────────────────────────────────────────────────────────

const EMPTY_FORM = {
  deal_name: '',
  company: '',
  contact_name: '',
  contact_search: '',
  amount: '',
  closing_date: '',
  stage: 'decouverte',
};

function NewDealDialog({ open, onClose, onCreated, prospects = [] }) {
  const { t } = useTranslation(['pages', 'common']);
  const [form, setForm] = useState(EMPTY_FORM);
  const [contactQuery, setContactQuery] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [creating, setCreating] = useState(false);

  const filteredProspects = useMemo(() => {
    if (!contactQuery.trim()) return [];
    const q = contactQuery.toLowerCase();
    return prospects
      .filter(p =>
        (p.prospect_name || '').toLowerCase().includes(q) ||
        (p.prospect_company || '').toLowerCase().includes(q)
      )
      .slice(0, 6);
  }, [contactQuery, prospects]);

  const set = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSelectContact = (prospect) => {
    setForm(prev => ({
      ...prev,
      contact_name: prospect.prospect_name || '',
      company: prev.company || prospect.prospect_company || '',
    }));
    setContactQuery(prospect.prospect_name || '');
    setShowContactDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.deal_name.trim()) {
      toast.error(t('pages:crm.toasts.nameRequired'));
      return;
    }
    setCreating(true);
    try {
      const payload = {
        deal_name: form.deal_name.trim(),
        company: form.company.trim() || null,
        contact_name: form.contact_name.trim() || null,
        amount: form.amount !== '' ? Number(form.amount) : null,
        closing_date: form.closing_date || null,
        stage: form.stage,
      };
      const created = { ...payload, id: Date.now().toString(), created_date: new Date().toISOString() };
      toast.success(t('pages:crm.toasts.created', { name: payload.deal_name }));
      setForm(EMPTY_FORM);
      setContactQuery('');
      onCreated(created);
    } catch (err) {
      toast.error(t('pages:crm.toasts.createError', { error: err.message ?? err }));
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setContactQuery('');
    setShowContactDropdown(false);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background rounded-2xl shadow-2xl border border-border w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">{t('pages:crm.newDeal.title')}</h2>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Deal name */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              {t('pages:crm.newDeal.nameLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.deal_name}
              onChange={e => set('deal_name', e.target.value)}
              placeholder={t('pages:crm.newDeal.namePlaceholder')}
              className="w-full h-9 px-3 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-muted-foreground/50"
              required
              autoFocus
            />
          </div>

          {/* Company + Contact (2 cols) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('common:field.company')}
              </label>
              <input
                type="text"
                value={form.company}
                onChange={e => set('company', e.target.value)}
                placeholder={t('pages:crm.newDeal.companyPlaceholder')}
                className="w-full h-9 px-3 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-muted-foreground/50"
              />
            </div>

            {/* Contact search */}
            <div className="relative">
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('pages:crm.fields.contact')}
              </label>
              <input
                type="text"
                value={contactQuery}
                onChange={e => {
                  setContactQuery(e.target.value);
                  set('contact_name', e.target.value);
                  setShowContactDropdown(true);
                }}
                onFocus={() => setShowContactDropdown(true)}
                onBlur={() => setTimeout(() => setShowContactDropdown(false), 150)}
                placeholder={t('pages:crm.newDeal.contactPlaceholder')}
                className="w-full h-9 px-3 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-muted-foreground/50"
              />
              {showContactDropdown && filteredProspects.length > 0 && (
                <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-background border border-border rounded-lg shadow-lg overflow-hidden">
                  {filteredProspects.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onMouseDown={() => handleSelectContact(p)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted text-left transition-colors"
                    >
                      <div className={`w-6 h-6 rounded-full ${avatarColor(p.prospect_name)} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                        {getInitials(p.prospect_name)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{p.prospect_name}</p>
                        {p.prospect_company && (
                          <p className="text-xs text-muted-foreground truncate">{p.prospect_company}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Amount + Closing date (2 cols) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('pages:crm.fields.amountWithCurrency')}
              </label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60" />
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.amount}
                  onChange={e => set('amount', e.target.value)}
                  placeholder="0"
                  className="w-full h-9 pl-8 pr-3 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-muted-foreground/50"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('pages:crm.fields.closingDate')}
              </label>
              <input
                type="date"
                value={form.closing_date}
                onChange={e => set('closing_date', e.target.value)}
                className="w-full h-9 px-3 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>

          {/* Stage */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              {t('pages:crm.fields.stage')}
            </label>
            <div className="relative">
              <select
                value={form.stage}
                onChange={e => set('stage', e.target.value)}
                className="w-full h-9 px-3 pr-8 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary appearance-none"
              >
                {DEAL_STAGES.map(s => (
                  <option key={s.id} value={s.id}>{t(s.labelKey)}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60 pointer-events-none" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 h-9 px-4 text-sm font-medium border border-border rounded-lg hover:bg-muted transition-colors text-muted-foreground"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={creating || !form.deal_name.trim()}
              className="flex-1 h-9 px-4 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {creating ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" />{t('pages:crm.newDeal.submitting')}</>
              ) : (
                <><Plus className="w-3.5 h-3.5" />{t('pages:crm.newDeal.submit')}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Deal Kanban column ───────────────────────────────────────────────────────

function DealCard({ deal }) {
  const stage = STAGE_BY_ID[deal.stage] || DEAL_STAGES[0];
  const initials = getInitials(deal.deal_name);
  const avColor = avatarColor(deal.deal_name);

  return (
    <div className="bg-card rounded-xl border border-border/50 p-3 hover:shadow-md hover:border-border transition-all">
      <div className="flex items-start gap-2.5">
        <div className={`flex-shrink-0 w-8 h-8 rounded-full ${avColor} flex items-center justify-center text-white text-xs font-semibold`}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate leading-tight">{deal.deal_name}</p>
          {deal.company && (
            <div className="flex items-center gap-1 mt-0.5">
              <Building2 className="w-3 h-3 text-muted-foreground/60 flex-shrink-0" />
              <p className="text-xs text-muted-foreground truncate">{deal.company}</p>
            </div>
          )}
          {deal.contact_name && (
            <div className="flex items-center gap-1 mt-0.5">
              <User className="w-3 h-3 text-muted-foreground/60 flex-shrink-0" />
              <p className="text-xs text-muted-foreground truncate">{deal.contact_name}</p>
            </div>
          )}
        </div>
      </div>
      {(deal.amount || deal.closing_date) && (
        <div className="mt-2 pt-2 border-t border-border/50 flex items-center justify-between gap-2">
          {deal.amount ? (
            <span className="text-xs font-semibold text-foreground">{formatAmount(deal.amount)}</span>
          ) : <span />}
          {deal.closing_date && (
            <div className="flex items-center gap-1 text-muted-foreground/60">
              <Calendar className="w-3 h-3" />
              <span className="text-xs">{formatDayMonthYear(deal.closing_date)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DealsPipelineBoard({ deals }) {
  const { t } = useTranslation(['pages', 'common']);
  const totalAmount = useMemo(() => deals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0), [deals]);
  const wonAmount = useMemo(() => deals.filter(d => d.stage === 'ferme_gagne').reduce((sum, d) => sum + (Number(d.amount) || 0), 0), [deals]);
  const wonCount = deals.filter(d => d.stage === 'ferme_gagne').length;
  const lostCount = deals.filter(d => d.stage === 'ferme_perdu').length;
  const activeCount = deals.filter(d => !['ferme_gagne', 'ferme_perdu'].includes(d.stage)).length;

  // W4 (B2-crm): the board is PipelineKanban, shared with Swoft.
  return (
    <div className="flex flex-col gap-4 w-full">
      <PipelineStats stats={[
        { id: 'total', icon: Target, tone: 'bg-blue-50 text-blue-600', value: deals.length, label: t('pages:crm.stats.totalDeals'), sub: t('pages:crm.stats.active', { count: activeCount }) },
        { id: 'pipeline', icon: DollarSign, tone: 'bg-violet-50 text-violet-600', value: formatAmount(totalAmount), label: t('pages:crm.stats.pipelineTotal') },
        { id: 'won', icon: Award, tone: 'bg-green-50 text-green-600', value: wonCount, label: t('pages:crm.stats.wonDeals'), sub: formatAmount(wonAmount) },
        { id: 'lost', icon: TrendingUp, tone: 'bg-red-50 text-red-500', value: lostCount, label: t('pages:crm.stats.lostDeals'), sub: deals.length > 0 ? t('pages:crm.stats.winRate', { rate: Math.round((wonCount / deals.length) * 100) }) : undefined },
      ]} />
      <PipelineKanban
        stages={DEAL_STAGES}
        items={deals}
        stageOf={(d) => d.stage}
        fallbackStage="decouverte"
        keyOf={(d) => d.id}
        renderCard={(d) => <DealCard deal={d} />}
      />
    </div>
  );
}

// ─── Deals list (table) view ──────────────────────────────────────────────────

function DealsListView({ deals }) {
  const { t } = useTranslation(['pages', 'common']);
  if (deals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <DollarSign className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium text-muted-foreground/60">{t('pages:crm.deals.emptyTitle')}</p>
        <p className="text-xs text-muted-foreground/40 mt-1">{t('pages:crm.deals.emptyHint')}</p>
      </div>
    );
  }

  return (
    <div className="relative overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">{t('pages:crm.fields.deal')}</th>
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3 hidden md:table-cell">{t('common:field.company')}</th>
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3 hidden lg:table-cell">{t('pages:crm.fields.contact')}</th>
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">{t('pages:crm.fields.stage')}</th>
            <th className="text-right text-xs font-semibold text-muted-foreground px-4 py-3">{t('pages:crm.fields.amount')}</th>
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3 hidden md:table-cell">{t('pages:crm.fields.closing')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {deals.map(deal => {
            const stage = STAGE_BY_ID[deal.stage] || DEAL_STAGES[0];
            return (
              <tr key={deal.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-medium text-foreground truncate max-w-[180px]">{deal.deal_name}</p>
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                  {deal.company || '—'}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                  {deal.contact_name || '—'}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border ${stage.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${stage.dot}`} />
                    {t(stage.labelKey)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-foreground">
                  {formatAmount(deal.amount)}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                  {formatDayMonthYear(deal.closing_date)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Contacts view ────────────────────────────────────────────────────────────

function ContactsView({ prospects, allCalls }) {
  const { t } = useTranslation(['pages', 'dialer', 'common']);
  const [search, setSearch] = useState('');

  const latestCallByPhone = useMemo(() => {
    const map = {};
    if (!allCalls?.length) return map;
    const sorted = [...allCalls].sort((a, b) =>
      new Date(b.started_at || b.created_date || 0) - new Date(a.started_at || a.created_date || 0)
    );
    sorted.forEach(c => {
      if (c.prospect_phone && !map[c.prospect_phone]) map[c.prospect_phone] = c;
    });
    return map;
  }, [allCalls]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return prospects;
    return prospects.filter(p =>
      (p.prospect_name || '').toLowerCase().includes(q) ||
      (p.prospect_company || '').toLowerCase().includes(q) ||
      (p.prospect_phone || '').includes(q)
    );
  }, [prospects, search]);

  if (prospects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Users className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium text-muted-foreground/60">{t('pages:crm.contacts.emptyTitle')}</p>
        <p className="text-xs text-muted-foreground/40 mt-1">{t('pages:crm.contacts.emptyHint')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Search */}
      <div className="relative max-w-sm">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('pages:crm.contacts.searchPlaceholder')}
          className="w-full h-9 pl-4 pr-4 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-muted-foreground/50"
        />
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(prospect => {
          const latestCall = latestCallByPhone[prospect.prospect_phone];
          const outcome = latestCall?.call_outcome;
          const callCount = allCalls.filter(c => c.prospect_phone === prospect.prospect_phone).length;
          const initials = getInitials(prospect.prospect_name);
          const avColor = avatarColor(prospect.prospect_name);

          const OUTCOME_LABELS = {
            meeting_booked: { cls: 'bg-green-100 text-green-700' },
            contacted: { cls: 'bg-blue-100 text-blue-700' },
            callback_requested: { cls: 'bg-amber-100 text-amber-700' },
            not_interested: { cls: 'bg-red-100 text-red-700' },
          };
          const outcomeInfo = OUTCOME_LABELS[outcome];

          return (
            <div key={prospect.id} className="bg-card border border-border/50 rounded-xl p-4 hover:shadow-md hover:border-border transition-all">
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full ${avColor} flex items-center justify-center text-white text-sm font-semibold`}>
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{prospect.prospect_name || '—'}</p>
                  {prospect.prospect_company && (
                    <p className="text-xs text-muted-foreground truncate">{prospect.prospect_company}</p>
                  )}
                </div>
                {outcomeInfo && (
                  <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${outcomeInfo.cls}`}>
                    {outcomeLabel(outcome, t)}
                  </span>
                )}
              </div>
              <div className="mt-3 space-y-1.5">
                {prospect.prospect_phone && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Phone className="w-3 h-3 flex-shrink-0" />
                    <span className="font-mono">{prospect.prospect_phone}</span>
                  </div>
                )}
                {prospect.prospect_email && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Mail className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{prospect.prospect_email}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span>{t('pages:crm.contacts.callCount', { count: callCount })}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && search && (
        <div className="text-center py-8 text-sm text-muted-foreground/60">
          {t('pages:crm.contacts.noResults', { query: search })}
        </div>
      )}
    </div>
  );
}

// ─── Tabs config ──────────────────────────────────────────────────────────────

const HUBSPOT_TABS = [
  { id: 'pipeline',     labelKey: 'pages:crm.tabs.pipeline',     icon: LayoutDashboard },
  { id: 'deals',        labelKey: 'pages:crm.tabs.deals',        icon: DollarSign },
  { id: 'contacts',     labelKey: 'pages:crm.tabs.contacts',     icon: Users },
  { id: 'activity',     labelKey: 'pages:crm.tabs.activity',     icon: Activity },
];

const SWOFT_TABS = [
  { id: 'pipeline',     labelKey: 'pages:crm.tabs.pipeline',      icon: LayoutDashboard },
  { id: 'opps',         labelKey: 'pages:crm.tabs.opportunities', icon: DollarSign },
  { id: 'activity',     labelKey: 'pages:crm.tabs.activity',      icon: Activity },
];

// W4 (B2-crm): the outcome → CRM mapping, admins only (the route is admin only).
const MAPPING_TAB = { id: 'mapping', labelKey: 'pages:crm.tabs.mapping', icon: ListChecks };

// ─── SWOFT Opportunity Pipeline ──────────────────────────────────────────────

const OPP_STAGES = [
  { id: 'NO_SHOW',        labelKey: 'pages:crm.oppStages.noShow',        color: 'bg-slate-100 text-slate-700 border-slate-200',     dot: 'bg-slate-400',   header: 'bg-slate-50',   border: 'border-slate-200',   badge: 'bg-slate-100 text-slate-600' },
  { id: 'PROSPECTING',    labelKey: 'pages:crm.oppStages.prospecting',   color: 'bg-blue-100 text-blue-700 border-blue-200',       dot: 'bg-blue-500',    header: 'bg-blue-50',    border: 'border-blue-200',    badge: 'bg-blue-100 text-blue-700' },
  { id: 'QUALIFICATION',  labelKey: 'pages:crm.oppStages.qualification', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500',  header: 'bg-indigo-50',  border: 'border-indigo-200',  badge: 'bg-indigo-100 text-indigo-700' },
  { id: 'NEEDS_ANALYSIS', labelKey: 'pages:crm.oppStages.needsAnalysis', color: 'bg-violet-100 text-violet-700 border-violet-200', dot: 'bg-violet-500',  header: 'bg-violet-50',  border: 'border-violet-200',  badge: 'bg-violet-100 text-violet-700' },
  { id: 'PROPOSAL',       labelKey: 'pages:crm.oppStages.proposal',      color: 'bg-amber-100 text-amber-700 border-amber-200',   dot: 'bg-amber-500',   header: 'bg-amber-50',   border: 'border-amber-200',   badge: 'bg-amber-100 text-amber-700' },
  { id: 'NEGOTIATION',    labelKey: 'pages:crm.oppStages.negotiation',   color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500',  header: 'bg-orange-50',  border: 'border-orange-200',  badge: 'bg-orange-100 text-orange-700' },
  { id: 'CLOSED_WON',     labelKey: 'pages:crm.oppStages.closedWon',     color: 'bg-green-100 text-green-700 border-green-200',    dot: 'bg-green-500',   header: 'bg-green-50',   border: 'border-green-200',   badge: 'bg-green-100 text-green-700' },
  { id: 'CLOSED_LOST',    labelKey: 'pages:crm.oppStages.closedLost',    color: 'bg-red-100 text-red-700 border-red-200',          dot: 'bg-red-500',     header: 'bg-red-50',     border: 'border-red-200',     badge: 'bg-red-100 text-red-700' },
];

/**
 * Swoft CRM channel / appointment / customer-status values are API enums.
 * They get a label from the catalog when we know them, and fall back to the
 * raw value so nothing ever renders blank.
 */
const COMM_CHANNEL_KEYS = { Phone: 'phone', Email: 'email', Meeting: 'meeting', Sms: 'sms' };
const APPOINTMENT_TYPE_KEYS = { Call: 'call', VideoCall: 'videoCall', Email: 'email', Meeting: 'meeting' };
const CUSTOMER_STATUS_KEYS = {
  Active: 'common:states.active',
  Inactive: 'common:states.inactive',
  Suspended: 'pages:crm.customers.suspended',
};

function commChannelLabel(channel, t) {
  const key = COMM_CHANNEL_KEYS[channel];
  return key ? t('pages:crm.detail.channels.' + key) : channel;
}

function appointmentTypeLabel(type, t) {
  const key = APPOINTMENT_TYPE_KEYS[type];
  return key ? t('pages:crm.detail.appointmentTypes.' + key) : type;
}

function customerStatusLabel(status, t) {
  const key = CUSTOMER_STATUS_KEYS[status || 'Active'];
  return key ? t(key) : status;
}

const OPP_STAGE_BY_ID = Object.fromEntries(OPP_STAGES.map(s => [s.id, s]));

function SwoftOppCard({ opp, onSelect }) {
  const contactName = [opp.contactFirstName, opp.contactLastName].filter(Boolean).join(' ') || opp.opportunityName;
  const initials = getInitials(contactName);
  const avColor = avatarColor(contactName);
  const amt = opp.amount?.amount || opp.actualAmount?.amount;

  return (
    <div
      className="bg-card rounded-xl border border-border/50 p-3 hover:shadow-md hover:border-border transition-all cursor-pointer"
      onClick={() => onSelect?.(opp)}
    >
      <div className="flex items-start gap-2.5">
        <div className={`flex-shrink-0 w-8 h-8 rounded-full ${avColor} flex items-center justify-center text-white text-xs font-semibold`}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate leading-tight">{contactName}</p>
          {opp.companyName && (
            <div className="flex items-center gap-1 mt-0.5">
              <Building2 className="w-3 h-3 text-muted-foreground/60 flex-shrink-0" />
              <p className="text-xs text-muted-foreground truncate">{opp.companyName}</p>
            </div>
          )}
        </div>
      </div>
      {(amt || opp.probability > 0 || opp.expectedCloseDate) && (
        <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
          <div className="flex items-center justify-between gap-2">
            {amt ? (
              <span className="text-xs font-semibold text-foreground flex items-center gap-1">
                <Euro className="w-3 h-3 text-muted-foreground/60" />
                {formatAmount(amt)}
              </span>
            ) : <span />}
            {opp.probability > 0 && (
              <span className="text-xs text-muted-foreground">{opp.probability}%</span>
            )}
          </div>
          {opp.expectedCloseDate && (
            <div className="flex items-center gap-1 text-muted-foreground/60">
              <Calendar className="w-3 h-3" />
              <span className="text-xs">{formatDayMonthYear(opp.expectedCloseDate)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Opportunity Detail Panel ────────────────────────────────────────────────

function CommunicationItem({ comm }) {
  const { t } = useTranslation(['pages', 'common']);
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-border/50 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-start gap-3 p-3 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex-shrink-0 p-1.5 rounded-lg bg-violet-50">
          {/* i18n-exempt-start: CRM enum values compared in code, never shown */}
          {comm.channel === 'Phone' ? <Phone className="w-3.5 h-3.5 text-violet-600" /> :
           comm.channel === 'Email' ? <Mail className="w-3.5 h-3.5 text-violet-600" /> :
           <Activity className="w-3.5 h-3.5 text-violet-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-foreground">{commChannelLabel(comm.channel, t)}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${comm.direction === 'Outbound' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
              {comm.direction === 'Outbound' ? t('pages:crm.detail.direction.outbound') : t('pages:crm.detail.direction.inbound')}
          {/* i18n-exempt-end */}
            </span>
            <span className="text-xs text-muted-foreground">{formatDayMonthYear(comm.createdAt)}</span>
          </div>
          {comm.subject && (
            <p className="text-xs font-medium text-foreground mt-1 truncate">{comm.subject}</p>
          )}
        </div>
        <ChevronRight className={`w-4 h-4 text-muted-foreground flex-shrink-0 mt-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-0 ml-[42px] border-t border-border/30">
          {comm.subject && (
            <p className="text-xs font-medium text-foreground mt-2">{comm.subject}</p>
          )}
          {comm.contentPreview && (
            <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{comm.contentPreview}</p>
          )}
          {comm.content && comm.content !== comm.contentPreview && (
            <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{comm.content}</p>
          )}
          {!comm.contentPreview && !comm.content && (
            <p className="text-xs text-muted-foreground/50 mt-2 italic">{t('pages:crm.detail.noContent')}</p>
          )}
        </div>
      )}
    </div>
  );
}

function CommunicationsList({ communications }) {
  const { t } = useTranslation(['pages']);
  return (
    <div className="bg-card border border-border/50 rounded-2xl p-5 space-y-3">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
        <Mail className="w-4 h-4 text-violet-500" />
        {t('pages:crm.detail.communications', { count: communications.length })}
      </h3>
      <div className="space-y-2">
        {communications.map((comm, i) => (
          <CommunicationItem key={comm.communicationId || i} comm={comm} />
        ))}
      </div>
    </div>
  );
}

function SwoftOppDetailPanel({ opp, onBack, clientEmail, customerId }) {
  const { t } = useTranslation(['pages', 'common']);
  // `r.data || r` rendait l'enveloppe { data, status } quand Swoft ne
  // connaissait pas l'opportunite : l'ecran affichait alors un objet sans nom
  // ni statut comme si c'en etait une. On garde l'entree de la liste, qui
  // porte deja les memes champs.
  const { data: detail, isLoading } = useQuery({
    queryKey: ['swoft-crm-opportunity-detail', opp.opportunityId, clientEmail, customerId],
    queryFn: () => api.functions
      .invoke('getSwoftCRMOpportunity', { opportunityId: opp.opportunityId, clientEmail, customerId })
      .then(r => (r?.data && typeof r.data === 'object' && r.data.opportunityId ? r.data : opp)),
    enabled: !!opp.opportunityId && !!clientEmail && !!customerId,
    initialData: opp,
  });

  const { data: appointments = [] } = useQuery({
    queryKey: ['swoft-crm-appointments', opp.opportunityId, clientEmail],
    queryFn: () => api.functions.invoke('getSwoftCRMAppointments', { opportunityId: opp.opportunityId, clientEmail }).then(r => r.data || r),
    enabled: !!opp.opportunityId && !!clientEmail,
  });

  const { data: communications = [] } = useQuery({
    queryKey: ['swoft-crm-communications', opp.opportunityId, clientEmail],
    queryFn: () => api.functions.invoke('getSwoftCRMCommunications', { opportunityId: opp.opportunityId, clientEmail }).then(r => r.data || r),
    enabled: !!opp.opportunityId && !!clientEmail,
  });

  const d = detail || opp;
  const contactName = [d.contactFirstName, d.contactLastName].filter(Boolean).join(' ');
  const stage = OPP_STAGE_BY_ID[d.stage] || OPP_STAGES[1];
  const amt = d.actualAmount?.amount || d.amount?.amount;
  const initials = getInitials(contactName || d.opportunityName);
  const avColor = avatarColor(contactName || d.opportunityName);

  return (
    <div className="space-y-5">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('pages:crm.detail.back')}
      </button>

      {/* Header */}
      <div className="bg-card border border-border/50 rounded-2xl p-5">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full ${avColor} flex items-center justify-center text-white text-sm font-bold`}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-foreground leading-tight">
              {contactName || d.opportunityName}
            </h2>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${stage.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${stage.dot}`} />
                {t(stage.labelKey)}
              </span>
              {d.companyName && (
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Building2 className="w-3.5 h-3.5" /> {d.companyName}
                </span>
              )}
              <span className="text-xs text-muted-foreground">{t('pages:crm.detail.probabilityInline', { value: d.probability })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-card border border-border/50 rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <DollarSign className="w-3.5 h-3.5" />
            <span className="text-xs">{t('pages:crm.fields.amount')}</span>
          </div>
          <p className="text-lg font-bold text-foreground">{formatAmount(amt)}</p>
        </div>
        <div className="bg-card border border-border/50 rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <TrendingUp className="w-3.5 h-3.5" />
            <span className="text-xs">{t('pages:crm.fields.probability')}</span>
          </div>
          <p className="text-lg font-bold text-foreground">{d.probability}%</p>
        </div>
        <div className="bg-card border border-border/50 rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Calendar className="w-3.5 h-3.5" />
            <span className="text-xs">{t('pages:crm.fields.closingDate')}</span>
          </div>
          <p className="text-lg font-bold text-foreground">{formatDayMonthYear(d.expectedCloseDate)}</p>
        </div>
        <div className="bg-card border border-border/50 rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <User className="w-3.5 h-3.5" />
            <span className="text-xs">{t('pages:crm.fields.owner')}</span>
          </div>
          <p className="text-lg font-bold text-foreground truncate">{d.owner || '—'}</p>
        </div>
      </div>

      {/* Info section */}
      <div className="bg-card border border-border/50 rounded-2xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-foreground">{t('pages:crm.fields.information')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {d.companyName && (
            <InfoField icon={Building2} label={t('common:field.company')} value={d.companyName} />
          )}
          {d.website && (
            <div>
              <span className="text-xs text-muted-foreground">{t('pages:crm.fields.website')}</span>
              <a href={d.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 hover:underline mt-0.5">
                <Globe className="w-3.5 h-3.5" /> {d.website} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
          {d.forecastCategory && (
            <InfoField icon={Target} label={t('pages:crm.fields.forecastCategory')} value={d.forecastCategory} />
          )}
          <InfoField icon={Calendar} label={t('pages:crm.fields.createdAt')} value={formatDayMonthYear(d.createdAt)} />
          {contactName && (
            <InfoField icon={User} label={t('pages:crm.fields.contact')} value={contactName} />
          )}
          {d.contactEmail && (
            <InfoField icon={Mail} label={t('common:field.email')} value={d.contactEmail} />
          )}
          {d.contactPhone && (
            <InfoField icon={Phone} label={t('common:field.phone')} value={d.contactPhone} />
          )}
          {d.particularInfo && (
            <div className="md:col-span-2">
              <span className="text-xs text-muted-foreground">{t('common:field.notes')}</span>
              <p className="text-sm text-foreground mt-0.5 whitespace-pre-wrap">{d.particularInfo}</p>
            </div>
          )}
          {d.winReason && (
            <div className="md:col-span-2">
              <span className="text-xs text-muted-foreground">{t('pages:crm.fields.winReason')}</span>
              <p className="text-sm text-foreground mt-0.5">{d.winReason}</p>
            </div>
          )}
          {d.lossReason && (
            <InfoField icon={AlertCircle} label={t('pages:crm.fields.lossReason')} value={d.lossReason} />
          )}
          {d.lostToCompetitor && (
            <InfoField icon={Users} label={t('pages:crm.fields.competitor')} value={d.lostToCompetitor} />
          )}
          {d.analysis && (
            <div className="md:col-span-2">
              <span className="text-xs text-muted-foreground">{t('pages:crm.fields.analysis')}</span>
              <p className="text-sm text-foreground mt-0.5 whitespace-pre-wrap">{d.analysis}</p>
            </div>
          )}
        </div>
      </div>

      {/* Communications */}
      {communications.length > 0 && (
        <CommunicationsList communications={communications} />
      )}

      {/* Use Cases */}
      {d.useCases && d.useCases.length > 0 && (
        <div className="bg-card border border-border/50 rounded-2xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            {t('pages:crm.detail.useCases', { count: d.useCases.length })}
          </h3>
          <div className="space-y-3">
            {d.useCases.map((uc, i) => (
              <div key={i} className="border border-border/50 rounded-xl p-4 space-y-2">
                <p className="text-sm font-semibold text-foreground">{uc.name || t('pages:crm.detail.useCaseFallback', { index: i + 1 })}</p>
                {uc.description && <p className="text-xs text-muted-foreground">{uc.description}</p>}
                {uc.painPoint && (
                  <p className="text-xs text-muted-foreground"><span className="font-medium">{t('pages:crm.detail.painPoint')}</span> {uc.painPoint}</p>
                )}
                {uc.specifications && (
                  <p className="text-xs text-muted-foreground"><span className="font-medium">{t('pages:crm.detail.specifications')}</span> {uc.specifications}</p>
                )}
                {uc.demoUrl && (
                  <a href={uc.demoUrl.trim()} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                    <ExternalLink className="w-3 h-3" /> {t('pages:crm.detail.viewDemo')}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Appointments */}
      {appointments.length > 0 && (
        <div className="bg-card border border-border/50 rounded-2xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-500" />
            {t('pages:crm.detail.appointments', { count: appointments.length })}
          </h3>
          <div className="space-y-2">
            {appointments.map((appt, i) => (
              <div key={appt.appointmentId || i} className="flex items-start gap-3 border border-border/50 rounded-xl p-3">
                <div className="flex-shrink-0 p-1.5 rounded-lg bg-blue-50">
                  {/* i18n-exempt-start: CRM enum values compared in code, never shown */}
                  {appt.appointmentType === 'Call' ? <Phone className="w-3.5 h-3.5 text-blue-600" /> :
                   appt.appointmentType === 'VideoCall' ? <Activity className="w-3.5 h-3.5 text-blue-600" /> :
                   appt.appointmentType === 'Email' ? <Mail className="w-3.5 h-3.5 text-blue-600" /> :
                   <Calendar className="w-3.5 h-3.5 text-blue-600" />}
                  {/* i18n-exempt-end */}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-foreground">{appointmentTypeLabel(appt.appointmentType, t)}</span>
                    <span className="text-xs text-muted-foreground">{formatDayMonthYear(appt.occurredAt)}</span>
                  </div>
                  {appt.content && (
                    <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3">{appt.content}</p>
                  )}
                  {appt.linkedEntityName && (
                    <span className="text-xs text-muted-foreground/60 mt-1 block">{appt.linkedEntityName}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

function InfoField({ icon: Icon, label, value }) {
  return (
    <div>
      <span className="text-xs text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </span>
      <p className="text-sm font-medium text-foreground mt-0.5">{value || '—'}</p>
    </div>
  );
}

/**
 * Pourquoi l'ecran est vide.
 *
 * 🚨 Kevin, 2026-10-01 : « je vois aucune opportunite, est-ce qu'on a perdu
 * les donnees ? ». Trois situations differentes rendaient exactement le meme
 * ecran vide et muet : aucun client selectionne (la requete ne partait meme
 * pas), le CRM de ce client pas connecte (409), et Swoft qui refuse ou tombe
 * (502). Un tableau vide se lit « il n'y a rien », ce qui est faux dans deux
 * cas sur trois. Il dit maintenant lequel.
 */
function SwoftReadProblem({ noCustomer, error }) {
  const { t } = useTranslation(['pages', 'common']);
  if (!noCustomer && !error) return null;
  const detail = noCustomer
    ? t('pages:crm.problem.noCustomer')
    : errorMessage(error, t, 'pages:crm.problem.readFailed');
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{detail}</span>
    </div>
  );
}

function SwoftPipelineBoard({ opportunities, onSelectOpp }) {
  const { t } = useTranslation(['pages', 'common']);
  const getAmt = (o) => o.amount?.amount || o.actualAmount?.amount || 0;
  const totalAmount = useMemo(() => (opportunities || []).reduce((sum, o) => sum + getAmt(o), 0), [opportunities]);
  const active = (opportunities || []).filter(o => o.stage !== 'CLOSED_WON' && o.stage !== 'CLOSED_LOST');
  const weightedAmount = useMemo(() => active.reduce((sum, o) => sum + (getAmt(o) * (o.probability || 0) / 100), 0), [active]);
  const wonOpps = (opportunities || []).filter(o => o.stage === 'CLOSED_WON');
  const lostOpps = (opportunities || []).filter(o => o.stage === 'CLOSED_LOST');
  const wonAmount = wonOpps.reduce((sum, o) => sum + getAmt(o), 0);
  const closedTotal = wonOpps.length + lostOpps.length;
  const winRate = closedTotal > 0 ? Math.round((wonOpps.length / closedTotal) * 100) : 0;

  if (!(opportunities || []).length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Target className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium text-muted-foreground/60">{t('pages:crm.opps.empty')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <PipelineStats stats={[
        { id: 'pipeline', icon: Target, tone: 'bg-blue-50 text-blue-600', value: formatAmount(totalAmount), label: t('pages:crm.stats.pipelineTotal'), sub: t('pages:crm.stats.opportunities', { count: (opportunities || []).length }) },
        { id: 'weighted', icon: DollarSign, tone: 'bg-violet-50 text-violet-600', value: formatAmount(weightedAmount), label: t('pages:crm.stats.weightedPipeline'), sub: t('pages:crm.stats.active', { count: active.length }) },
        { id: 'won', icon: Award, tone: 'bg-green-50 text-green-600', value: wonOpps.length, label: t('pages:crm.stats.wonDeals'), sub: formatAmount(wonAmount) },
        { id: 'lost', icon: TrendingUp, tone: 'bg-red-50 text-red-500', value: lostOpps.length, label: t('pages:crm.stats.lostDeals'), sub: t('pages:crm.stats.winRate', { rate: winRate }) },
      ]} />
      <PipelineKanban
        stages={OPP_STAGES}
        items={opportunities || []}
        stageOf={(o) => o.stage}
        fallbackStage="PROSPECTING"
        keyOf={(o) => o.opportunityId}
        renderCard={(o) => <SwoftOppCard opp={o} onSelect={onSelectOpp} />}
      />
    </div>
  );
}

function SwoftOppsListView({ opportunities, onSelectOpp }) {
  const { t } = useTranslation(['pages', 'common']);
  if (!(opportunities || []).length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <DollarSign className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium text-muted-foreground/60">{t('pages:crm.opps.empty')}</p>
      </div>
    );
  }

  return (
    <div className="relative overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">{t('pages:crm.fields.opportunity')}</th>
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3 hidden md:table-cell">{t('common:field.company')}</th>
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3 hidden lg:table-cell">{t('pages:crm.fields.contact')}</th>
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">{t('pages:crm.fields.stage')}</th>
            <th className="text-right text-xs font-semibold text-muted-foreground px-4 py-3">{t('pages:crm.fields.amount')}</th>
            <th className="text-right text-xs font-semibold text-muted-foreground px-4 py-3 hidden md:table-cell">{t('pages:crm.fields.probabilityShort')}</th>
            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3 hidden md:table-cell">{t('pages:crm.fields.closing')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {(opportunities || []).map(opp => {
            const stage = OPP_STAGE_BY_ID[opp.stage] || OPP_STAGES[1];
            const contactName = [opp.contactFirstName, opp.contactLastName].filter(Boolean).join(' ');
            const amt = opp.amount?.amount || opp.actualAmount?.amount;
            return (
              <tr key={opp.opportunityId} className="hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => onSelectOpp?.(opp)}>
                <td className="px-4 py-3">
                  <p className="font-medium text-foreground truncate max-w-[180px]">{opp.opportunityName}</p>
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                  {opp.companyName || '—'}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                  {contactName || '—'}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border ${stage.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${stage.dot}`} />
                    {t(stage.labelKey)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-foreground">
                  {formatAmount(amt)}
                </td>
                <td className="px-4 py-3 text-right text-muted-foreground hidden md:table-cell">
                  {opp.probability > 0 ? `${opp.probability}%` : '—'}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                  {formatDayMonthYear(opp.expectedCloseDate)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// ─── No CRM configured view ──────────────────────────────────────────────────

function NoCRMView() {
  const { t } = useTranslation(['pages']);
  const navigate = useNavigate();
  return (
    <motion.div
      key="no-crm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <div className="w-16 h-16 rounded-2xl bg-muted/60 flex items-center justify-center mb-4">
        <Plug className="w-7 h-7 text-muted-foreground/40" />
      </div>
      <h2 className="text-lg font-semibold text-foreground mb-1">{t('pages:crm.noCrm.title')}</h2>
      <p className="text-sm text-muted-foreground max-w-md mb-5">
        {t('pages:crm.noCrm.description')}
      </p>
      {/* L'ecran disait ou aller sans y mener. L'onglet « Integrations » de
          cette page a disparu avec la separation : se connecter vit dans les
          reglages, lire le pipeline vit ici. */}
      <button
        type="button"
        onClick={() => navigate('/ParallelDialer/settings?section=integrations')}
        className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-accent/50 transition-colors"
      >
        <Plug className="w-4 h-4" />
        {t('pages:crm.noCrm.connect')}
      </button>
    </motion.div>
  );
}

// ─── Main CRMScreen ──────────────────────────────────────────────────────────

export default function CRMScreen() {
  const { t } = useTranslation(['pages', 'dialer', 'common']);
  // ADR-024 — Customer-scoped views. The CRM screen shows the active
  // Customer's prospects + calls only.
  const { callerData, filteredAllCalls: allCalls, filteredProspectQueue: prospectQueue, selectedCustomerId } = useDialer();
  const clientEmail = callerData?.client_email || callerData?.email;

  // ADR-031 — CRM provider is per-Customer. Read it from the active Customer
  // (selected in the sidebar) rather than the legacy tenant-wide Client doc.
  const { data: customers = [] } = useQuery({
    queryKey: ['customers', { activeOnly: true }],
    queryFn: ({ signal }) => listCustomers({ activeOnly: true, signal }),
    enabled: !!callerData,
    staleTime: 60_000,
  });
  const currentCustomer = customers.find((c) => c.id === selectedCustomerId);
  // HubSpot hidden from the UI (AUDIT-INT-08, src/lib/hubspotUi.ts): a
  // HubSpot customer reads as 'none' here and gets the "no CRM" screen.
  const activeCrmProvider = crmProviderShown(currentCustomer?.active_crm_provider);

  const [activeTab, setActiveTab] = useState('pipeline');
  const isAdmin = useAuthStore((st) => st.user?.isSuperAdmin === true || st.activeMembership?.role === 'tenant_admin');
  const [showNewDeal, setShowNewDeal] = useState(false);
  const [deals, setDeals] = useState([]);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const dealsLoading = false;
  const dealsError = null;

  // Reset tab when provider changes
  useEffect(() => {
    if (activeCrmProvider === 'swoft') setActiveTab('pipeline');
    else if (activeCrmProvider === 'hubspot') setActiveTab('pipeline');
    setSelectedOpp(null);
  }, [activeCrmProvider]);

  // Clear detail view when switching tabs
  useEffect(() => {
    setSelectedOpp(null);
  }, [activeTab]);

  const handleDealCreated = useCallback((newDeal) => {
    setShowNewDeal(false);
    setDeals(prev => [...prev, newDeal]);
  }, []);

  // SWOFT CRM data
  // Le CRM Swoft rend un tableau JSON nu. Tout le reste de cet ecran l'admet
  // sans le verifier : `opportunities.forEach` sur un objet fait tomber la
  // page entiere sur un ecran blanc, et cette page depend d'un service qu'on
  // n'a pas sous la main. On normalise ICI, a l'entree, une seule fois.
  const enTableau = (x) => (Array.isArray(x) ? x : []);

  const { data: swoftOpportunities = [], isLoading: swoftOppsLoading, error: swoftOppsError } = useQuery({
    queryKey: ['swoft-crm-opportunities', clientEmail, selectedCustomerId],
    queryFn: () => api.functions
      .invoke('getSwoftCRMOpportunities', { clientEmail, customerId: selectedCustomerId })
      .then(r => enTableau(r.data)),
    enabled: activeCrmProvider === 'swoft' && !!clientEmail && !!selectedCustomerId,
  });

  // L'historique du CRM : les echanges de TOUTE l'organisation du client, pas
  // seulement ceux que Revcall a pousses. C'est la seule facon de voir ce que
  // l'equipe Swoft a fait de son cote.
  const { data: swoftActivity = [], isLoading: swoftActivityLoading } = useQuery({
    queryKey: ['swoft-crm-activity', clientEmail, selectedCustomerId],
    queryFn: () => api.functions
      .invoke('listSwoftCRMCommunications', { clientEmail, customerId: selectedCustomerId, limit: 100 })
      .then(r => enTableau(r.data)),
    enabled: activeCrmProvider === 'swoft' && !!clientEmail && !!selectedCustomerId,
  });

  // L'onglet « Clients » a ete retire (Kevin, 2026-10-01) : les clients du CRM
  // ne sont pas ce qu'un appelant vient chercher ici, et l'ecran en comptait
  // deja un autre, le selecteur de client de la barre laterale.

  // No CRM configured
  if (activeCrmProvider === 'none') {
    return <NoCRMView />;
  }

  const tabs = activeCrmProvider === 'swoft' ? (isAdmin ? [...SWOFT_TABS, MAPPING_TAB] : SWOFT_TABS) : HUBSPOT_TABS;
  // REV-W4-F14: a tab that the current customer does not offer (« Correspondance »
  // after switching to a HubSpot customer, or to a non-admin view) falls back.
  const currentTab = resolveCrmTab(tabs, activeTab);
  const showNewDealButton = activeCrmProvider === 'hubspot' && (currentTab === 'pipeline' || currentTab === 'deals');

  return (
    <>
      {activeCrmProvider === 'hubspot' && (
        <NewDealDialog
          open={showNewDeal}
          onClose={() => setShowNewDeal(false)}
          onCreated={handleDealCreated}
          prospects={prospectQueue || []}
        />
      )}

      <motion.div
        key="crm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="flex flex-col gap-4 h-full"
      >
        {showNewDealButton && (
          <div className="flex justify-end">
            <button
              onClick={() => setShowNewDeal(true)}
              className="flex items-center gap-2 h-9 px-4 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('pages:crm.newDeal.button')}
            </button>
          </div>
        )}

        {/* Tabs (REV-W4-F15: WAI-ARIA tabs, arrow keys) */}
        <CrmTabBar tabs={tabs} active={currentTab} onChange={setActiveTab} label={t('pages:crm.tabs.label')} t={t} />

        {/* Tab content */}
        <div role="tabpanel" id={crmPanelId('crm')} aria-labelledby={crmTabId('crm', currentTab)} tabIndex={0} className="flex-1 min-h-0 overflow-y-auto">
          {/* ── HubSpot mode ── */}
          {activeCrmProvider === 'hubspot' && (
            <>
              {currentTab === 'pipeline' && (
                <div className="space-y-4">
                  {dealsLoading ? (
                    <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{t('pages:crm.loading.deals')}</span>
                    </div>
                  ) : dealsError ? (
                    <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{t('pages:crm.errors.dealsLoad', { error: dealsError.message })}</span>
                    </div>
                  ) : (
                    <DealsPipelineBoard deals={deals} />
                  )}
                </div>
              )}
              {currentTab === 'deals' && (
                <div>
                  {dealsLoading ? (
                    <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{t('pages:crm.loading.deals')}</span>
                    </div>
                  ) : (
                    <DealsListView deals={deals} />
                  )}
                </div>
              )}
              {currentTab === 'contacts' && (
                <ContactsView prospects={prospectQueue || []} allCalls={allCalls || []} />
              )}
              {currentTab === 'activity' && (
                <CRMActivityFeed allCalls={allCalls || []} prospects={prospectQueue || []} />
              )}
            </>
          )}

          {/* ── SWOFT CRM mode ── */}
          {activeCrmProvider === 'swoft' && (
            <>
              {/* Le bandeau « donnees locales a Revcall » est parti avec la
                  cause : GET /api/swoft-crm/opportunities lit desormais le
                  pipeline CHEZ Swoft (SwoftOpportunityService). */}
              {selectedOpp ? (
                <SwoftOppDetailPanel
                  customerId={selectedCustomerId}
                  opp={selectedOpp}
                  onBack={() => setSelectedOpp(null)}
                  clientEmail={clientEmail}
                />
              ) : (
                <>
              {currentTab === 'pipeline' && (
                <div className="space-y-4">
                  <SwoftReadProblem noCustomer={!selectedCustomerId} error={swoftOppsError} />
                  {swoftOppsLoading ? (
                    <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{t('pages:crm.loading.pipeline')}</span>
                    </div>
                  ) : (
                    <SwoftPipelineBoard opportunities={swoftOpportunities} onSelectOpp={setSelectedOpp} />
                  )}
                </div>
              )}
              {currentTab === 'opps' && (
                <div className="space-y-4">
                  <SwoftReadProblem noCustomer={!selectedCustomerId} error={swoftOppsError} />
                  {swoftOppsLoading ? (
                    <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{t('pages:crm.loading.opps')}</span>
                    </div>
                  ) : (
                    <SwoftOppsListView opportunities={swoftOpportunities} onSelectOpp={setSelectedOpp} />
                  )}
                </div>
              )}
              {currentTab === 'mapping' && isAdmin && <OutcomeMappingPanel customerId={selectedCustomerId} />}
              {currentTab === 'activity' && (
                <SwoftActivityFeed communications={swoftActivity} loading={swoftActivityLoading} />
              )}
                </>
              )}
            </>
          )}
        </div>
      </motion.div>
    </>
  );
}
